import { ChangeDetectionStrategy, Component, booleanAttribute, computed, input } from '@angular/core';
import { RouterLink } from '@angular/router';
import { Icon } from '../icon/icon';
import { TrackCtaDirective } from '../../../core/directives/track-cta.directive';
import { splitHref } from '../../../core/utils/split-href';
import type { CtaLocation } from '../../../core/models/analytics.model';
import type { BillingTerm } from '../../../core/models/pricing.model';
import type { Cta } from '../../../core/models/product.model';

/**
 * One call to action, rendered as an in-app route or an outbound link.
 *
 * Outbound links carry rel="noopener" and a visible arrow so it is obvious
 * before the click that the destination leaves the site — which for "get
 * started" means the product's own onboarding.
 */
@Component({
  selector: 'av-cta',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterLink, Icon, TrackCtaDirective],
  template: `
    @if (cta().external) {
      <a
        [href]="cta().href"
        [class]="classes()"
        target="_blank"
        rel="noopener noreferrer"
        [avTrackCta]="cta().id"
        [ctaLocation]="location()"
        [ctaPlan]="cta().planCode"
        [ctaTerm]="billingTerm()"
      >
        <span>{{ cta().label }}</span>
        <span class="btn__icon"><av-icon name="arrow-up-right" [size]="iconSize()" /></span>
      </a>
    } @else {
      <a
        [routerLink]="path()"
        [fragment]="fragment()"
        [queryParams]="query()"
        [class]="classes()"
        [avTrackCta]="cta().id"
        [ctaLocation]="location()"
        [ctaPlan]="cta().planCode"
      >
        <span>{{ cta().label }}</span>
        @if (arrow()) {
          <span class="btn__icon btn__icon--arrow"><av-icon name="arrow-right" [size]="iconSize()" /></span>
        }
      </a>
    }
  `,
})
export class CtaLink {
  readonly cta = input.required<Cta>();
  readonly size = input<'sm' | 'md' | 'lg'>('md');
  readonly block = input(false, { transform: booleanAttribute });
  readonly arrow = input(true, { transform: booleanAttribute });
  /** Where the CTA sits, reported as `cta_location`; without one the click is not reported. */
  readonly location = input<CtaLocation>();
  /** The pricing page's term, reported with a plan's onboarding click. */
  readonly billingTerm = input<BillingTerm>();

  protected readonly classes = computed(() => {
    const variant = this.cta().variant ?? 'primary';
    if (variant === 'link') return 'link';
    const size = this.size();
    return [
      'btn',
      `btn--${variant}`,
      size === 'lg' ? 'btn--lg' : size === 'sm' ? 'btn--sm' : '',
      this.block() ? 'btn--block' : '',
    ]
      .filter(Boolean)
      .join(' ');
  });

  /** The size every other `.link` on the site pairs with its arrow. */
  protected readonly iconSize = computed(() => (this.cta().variant === 'link' ? 14 : 16));

  private readonly parsed = computed(() => splitHref(this.cta().href));

  protected readonly path = computed(() => this.parsed().path);
  protected readonly fragment = computed(() => this.parsed().fragment);
  protected readonly query = computed(() => this.parsed().query);
}
